/* THUNDERNOISE — Wishlist (localStorage)
 * Public API: window.TNWishlist
 *   .get()        -> [{handle, addedAt}]
 *   .has(handle)  -> boolean
 *   .add(handle)
 *   .remove(handle)
 *   .toggle(handle)
 *   .count()
 *   .clear()
 * Eventos emitidos en window:
 *   'tn:wishlist:changed' (detail: { items, action, handle })
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'tn_wishlist_v1';
  var MAX_ITEMS = 200;

  function safeRead() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (it) {
        return it && typeof it.handle === 'string' && it.handle.length > 0;
      });
    } catch (e) {
      return [];
    }
  }

  function safeWrite(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      return true;
    } catch (e) {
      return false;
    }
  }

  function emit(action, handle, items) {
    window.dispatchEvent(
      new CustomEvent('tn:wishlist:changed', {
        detail: { action: action, handle: handle, items: items },
      })
    );
  }

  var TNWishlist = {
    get: function () {
      return safeRead();
    },
    count: function () {
      return safeRead().length;
    },
    has: function (handle) {
      if (!handle) return false;
      return safeRead().some(function (it) {
        return it.handle === handle;
      });
    },
    add: function (handle) {
      if (!handle) return false;
      var items = safeRead();
      if (items.some(function (it) { return it.handle === handle; })) return false;
      if (items.length >= MAX_ITEMS) items.shift();
      items.push({ handle: handle, addedAt: Date.now() });
      safeWrite(items);
      emit('add', handle, items);
      return true;
    },
    remove: function (handle) {
      if (!handle) return false;
      var items = safeRead();
      var next = items.filter(function (it) { return it.handle !== handle; });
      if (next.length === items.length) return false;
      safeWrite(next);
      emit('remove', handle, next);
      return true;
    },
    toggle: function (handle) {
      if (!handle) return false;
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    },
    clear: function () {
      safeWrite([]);
      emit('clear', null, []);
    },
  };

  window.TNWishlist = TNWishlist;

  /* -------------------------------------------------------------
   * Hydration: bind every [data-tn-wishlist-toggle] button
   * ----------------------------------------------------------- */
  function syncButton(btn) {
    var handle = btn.getAttribute('data-tn-wishlist-toggle');
    if (!handle) return;
    var active = TNWishlist.has(handle);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('is-active', active);
    var labelAdd = btn.getAttribute('data-label-add');
    var labelRemove = btn.getAttribute('data-label-remove');
    var labelEl = btn.querySelector('[data-tn-wishlist-label]');
    if (labelEl && labelAdd && labelRemove) {
      labelEl.textContent = active ? labelRemove : labelAdd;
    }
    var ariaLabel = active ? labelRemove : labelAdd;
    if (ariaLabel) btn.setAttribute('aria-label', ariaLabel);
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-tn-wishlist-toggle]').forEach(syncButton);
  }

  function syncCounters() {
    var n = TNWishlist.count();
    document.querySelectorAll('[data-tn-wishlist-count]').forEach(function (el) {
      el.textContent = String(n);
      el.classList.toggle('is-empty', n === 0);
      var bubble = el.closest('[data-tn-wishlist-bubble]');
      if (bubble) bubble.classList.toggle('is-empty', n === 0);
    });
  }

  function handleClick(e) {
    var btn = e.target.closest('[data-tn-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.getAttribute('data-tn-wishlist-toggle');
    TNWishlist.toggle(handle);
    btn.classList.add('is-animating');
    setTimeout(function () { btn.classList.remove('is-animating'); }, 350);
  }

  function init() {
    syncAllButtons();
    syncCounters();
    document.addEventListener('click', handleClick);
    window.addEventListener('tn:wishlist:changed', function () {
      syncAllButtons();
      syncCounters();
    });
    // Cross-tab sync
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) {
        syncAllButtons();
        syncCounters();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* -------------------------------------------------------------
   * Wishlist page renderer
   * Activates only on elements with data-tn-wishlist-page
   * ----------------------------------------------------------- */
  function moneyFormat(cents) {
    var amount = (cents / 100).toFixed(2);
    var locale = (document.documentElement.lang || 'es').toLowerCase();
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: window.Shopify && Shopify.currency && Shopify.currency.active
          ? Shopify.currency.active
          : 'EUR',
      }).format(cents / 100);
    } catch (e) {
      return amount + ' €';
    }
  }

  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      if (!r.ok) throw new Error('not_found');
      return r.json();
    });
  }

  function getI18n(root) {
    var d = root && root.dataset ? root.dataset : {};
    return {
      addToCart: d.i18nAddToCart || 'Add to cart',
      soldOut:   d.i18nSoldOut   || 'Sold out',
      remove:    d.i18nRemove    || 'Remove',
      adding:    d.i18nAdding    || 'Adding…',
      added:     d.i18nAdded     || 'Added ✓',
      error:     d.i18nError     || 'Error',
      addAll:    d.i18nAddAll    || 'Add all to cart',
    };
  }

  function renderItem(product, t) {
    var img = product.featured_image
      ? '<a href="' + product.url + '" class="tn-wishlist-card__media"><img src="' + product.featured_image + '" alt="' + escapeAttr(product.title) + '" loading="lazy" /></a>'
      : '<a href="' + product.url + '" class="tn-wishlist-card__media tn-wishlist-card__media--empty"></a>';
    var priceHTML = '';
    if (product.compare_at_price && product.compare_at_price > product.price) {
      priceHTML =
        '<span class="tn-wishlist-card__price tn-wishlist-card__price--sale">' + moneyFormat(product.price) + '</span>' +
        '<span class="tn-wishlist-card__price tn-wishlist-card__price--compare">' + moneyFormat(product.compare_at_price) + '</span>';
    } else {
      priceHTML = '<span class="tn-wishlist-card__price">' + moneyFormat(product.price) + '</span>';
    }
    var available = product.available !== false;
    var atcHTML = available
      ? '<button type="button" class="button tn-wishlist-card__atc" data-tn-wishlist-atc data-variant-id="' + product.variants[0].id + '" data-text-default="' + escapeAttr(t.addToCart) + '">' + escapeHtml(t.addToCart) + '</button>'
      : '<button type="button" class="button button-secondary tn-wishlist-card__atc" disabled>' + escapeHtml(t.soldOut) + '</button>';
    return (
      '<article class="tn-wishlist-card" data-handle="' + escapeAttr(product.handle) + '">' +
        img +
        '<div class="tn-wishlist-card__body">' +
          '<a href="' + product.url + '" class="tn-wishlist-card__title">' + escapeHtml(product.title) + '</a>' +
          '<div class="tn-wishlist-card__prices">' + priceHTML + '</div>' +
          '<div class="tn-wishlist-card__actions">' +
            atcHTML +
            '<button type="button" class="tn-wishlist-card__remove" data-tn-wishlist-remove="' + escapeAttr(product.handle) + '" aria-label="' + escapeAttr(t.remove) + '">' +
              '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</article>'
    );
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  function escapeAttr(str) {
    return escapeHtml(str).replace(/"/g, '&quot;');
  }

  function getCartSectionIds() {
    var ids = [];
    var seen = {};
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var sid = el.getAttribute('data-section-id');
      if (sid && !seen[sid]) { ids.push(sid); seen[sid] = true; }
    });
    return ids;
  }

  function dispatchCartUpdate(itemCount, sections, sourceId) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: {},
        sourceId: sourceId || 'tn-wishlist',
        data: {
          source: 'product-form-component',
          itemCount: itemCount,
          sections: sections,
        },
      },
    }));
  }

  function postCartAdd(items) {
    var sectionIds = getCartSectionIds();
    var body = { items: items };
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (b) { return { ok: r.ok, body: b }; });
    });
  }

  function addToCart(variantId, btn, t) {
    btn.disabled = true;
    var original = btn.getAttribute('data-text-default') || btn.textContent;
    btn.textContent = t.adding;
    return postCartAdd([{ id: parseInt(variantId, 10), quantity: 1 }])
      .then(function (res) {
        if (!res.ok) throw res.body;
        btn.textContent = t.added;
        var sections = res.body && res.body.sections ? res.body.sections : undefined;
        dispatchCartUpdate(1, sections, 'tn-wishlist-' + variantId);
        setTimeout(function () {
          btn.textContent = original;
          btn.disabled = false;
        }, 1500);
      })
      .catch(function (err) {
        console.error('[tn-wishlist] add failed:', err);
        btn.textContent = t.error;
        setTimeout(function () {
          btn.textContent = original;
          btn.disabled = false;
        }, 1500);
      });
  }

  function addAllToCart(root, t) {
    var atcButtons = root.querySelectorAll('.tn-wishlist-card__atc[data-variant-id]:not([disabled])');
    if (atcButtons.length === 0) return;

    var items = Array.prototype.map.call(atcButtons, function (b) {
      return { id: parseInt(b.getAttribute('data-variant-id'), 10), quantity: 1 };
    });

    var bulkBtn = root.querySelector('[data-tn-wishlist-add-all]');
    var original = bulkBtn ? bulkBtn.textContent : '';
    if (bulkBtn) {
      bulkBtn.disabled = true;
      bulkBtn.textContent = t.adding;
    }

    return postCartAdd(items)
      .then(function (res) {
        if (!res.ok) throw res.body;
        if (bulkBtn) bulkBtn.textContent = t.added;
        var sections = res.body && res.body.sections ? res.body.sections : undefined;
        dispatchCartUpdate(items.length, sections, 'tn-wishlist-add-all');
        setTimeout(function () {
          if (bulkBtn) {
            bulkBtn.textContent = original;
            bulkBtn.disabled = false;
          }
        }, 2000);
      })
      .catch(function (err) {
        console.error('[tn-wishlist] add all failed:', err);
        if (bulkBtn) {
          bulkBtn.textContent = t.error;
          setTimeout(function () {
            bulkBtn.textContent = original;
            bulkBtn.disabled = false;
          }, 2000);
        }
      });
  }

  function toggleBulkVisibility(root) {
    var bulk = root.querySelector('[data-tn-wishlist-bulk]');
    if (!bulk) return;
    var availableCount = root.querySelectorAll('.tn-wishlist-card__atc[data-variant-id]:not([disabled])').length;
    bulk.hidden = availableCount === 0;
  }

  function renderPage(root) {
    var grid = root.querySelector('[data-tn-wishlist-grid]');
    var empty = root.querySelector('[data-tn-wishlist-empty]');
    var loading = root.querySelector('[data-tn-wishlist-loading]');
    var bulk = root.querySelector('[data-tn-wishlist-bulk]');
    if (!grid) return;

    var t = getI18n(root);

    var items = TNWishlist.get();
    if (items.length === 0) {
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = false;
      if (bulk) bulk.hidden = true;
      grid.innerHTML = '';
      return;
    }

    if (empty) empty.hidden = true;
    if (loading) loading.hidden = false;
    grid.innerHTML = '';

    var ordered = items.slice().sort(function (a, b) { return (b.addedAt || 0) - (a.addedAt || 0); });

    Promise.all(
      ordered.map(function (it) {
        return fetchProduct(it.handle)
          .then(function (p) { return { ok: true, product: p, handle: it.handle }; })
          .catch(function () { return { ok: false, handle: it.handle }; });
      })
    ).then(function (results) {
      if (loading) loading.hidden = true;
      var html = '';
      var missing = [];
      results.forEach(function (r) {
        if (r.ok) html += renderItem(r.product, t);
        else missing.push(r.handle);
      });
      grid.innerHTML = html;
      missing.forEach(function (h) { TNWishlist.remove(h); });
      if (grid.children.length === 0 && empty) empty.hidden = false;
      toggleBulkVisibility(root);
    });
  }

  function bindPage(root) {
    root.addEventListener('click', function (e) {
      var t = getI18n(root);
      var rm = e.target.closest('[data-tn-wishlist-remove]');
      if (rm) {
        e.preventDefault();
        var handle = rm.getAttribute('data-tn-wishlist-remove');
        var card = rm.closest('.tn-wishlist-card');
        if (card) {
          card.classList.add('is-removing');
          setTimeout(function () { TNWishlist.remove(handle); }, 200);
        } else {
          TNWishlist.remove(handle);
        }
        return;
      }
      var atc = e.target.closest('[data-tn-wishlist-atc]');
      if (atc) {
        e.preventDefault();
        var variantId = atc.getAttribute('data-variant-id');
        if (variantId) addToCart(variantId, atc, t);
        return;
      }
      var bulk = e.target.closest('[data-tn-wishlist-add-all]');
      if (bulk) {
        e.preventDefault();
        addAllToCart(root, t);
      }
    });

    window.addEventListener('tn:wishlist:changed', function (ev) {
      if (!ev.detail) return;
      if (ev.detail.action === 'remove') {
        var card = root.querySelector('.tn-wishlist-card[data-handle="' + cssEscape(ev.detail.handle) + '"]');
        if (card) card.remove();
        var grid = root.querySelector('[data-tn-wishlist-grid]');
        var empty = root.querySelector('[data-tn-wishlist-empty]');
        if (grid && grid.children.length === 0 && empty) empty.hidden = false;
        toggleBulkVisibility(root);
      } else if (ev.detail.action === 'add' || ev.detail.action === 'clear') {
        renderPage(root);
      }
    });
  }

  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function initPages() {
    document.querySelectorAll('[data-tn-wishlist-page]').forEach(function (root) {
      bindPage(root);
      renderPage(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPages);
  } else {
    initPages();
  }
})();
